/**
 * surveyQuestions_v2.js — CHI Study 1 (counterbalanced split + day-7 probe)
 *
 * Same question-object schema as the deployed surveyQuestions.js:
 *   scale | radio | text | multiple_choice, with min/max/minLabel/maxLabel,
 *   options, optional, subtitle, showIf {key, value}, placeholder.
 *
 * FLOW (new design):
 *   SCREENING (sync, proctored):  warm-up coding task  -> WARMUP_FOLLOWUP_QUESTIONS
 *   MAIN (async, ~80 min):
 *     PRE_QUIZ_QUESTIONS (concept check, no AI)
 *     -> A1/B1 with AI tutor, fixed 20-min boxes, order counterbalanced
 *        (taskDifficulty('a1'|'b1') after each)
 *     -> MID_SESSION_QUESTIONS (per-domain confidence, BEFORE any transfer)
 *     -> immediate transfer task, no AI (taskDifficulty('transfer_immediate'))
 *     -> POST_TASK_QUESTIONS
 *     -> quiz (no AI)  -> POST_SURVEY_QUESTIONS  -> probe booking (platform gate)
 *   PROBE (day 6–8, ~20 min):
 *     probePre(domain) -> delayed transfer task, no AI
 *     -> taskDifficulty('transfer_delayed') -> PROBE_POST_QUESTIONS
 *
 * Changes vs. v1, and why:
 *   - Confidence/understanding split PER DOMAIN: the counterbalance tests one
 *     domain at day 7, so a combined "understand Trio?" item can't pair with
 *     the delayed outcome.
 *   - Warm-up follow-up + per-task difficulty items added (S&T Fig. 10 measured
 *     stage difficulty; we take it immediately per block instead of retrospectively).
 *   - Probe blocks are new. Honesty item now names the no-AI blocks explicitly
 *     and has a probe-scoped twin.
 *   - Two attention checks in the exit survey (pre-registered rule: fail BOTH
 *     -> survey-derived measures excluded).
 *   - v1 items kept verbatim where unchanged (demographics, reliance, honesty
 *     subtitle, reflections, interview opt-in).
 */

// ---------------------------------------------------------------------------
// SCREENING — after the proctored warm-up (one item; feeds difficulty calibration)
// ---------------------------------------------------------------------------
export const WARMUP_FOLLOWUP_QUESTIONS = [
  {
    key: 'difficulty_warmup',
    label: 'How difficult was the warm-up exercise for you?',
    type: 'scale', min: 1, max: 7,
    minLabel: 'Very easy', maxLabel: 'Very difficult'
  }
]

// ---------------------------------------------------------------------------
// MAIN — concept check at session start (objective baseline; language-general,
// deliberately NOT Trio-specific: participants are screened to be new to Trio).
// Items 1–5 are v1 verbatim; 6–8 added to balance error-handling coverage
// (v1 was 4 concurrency-leaning : 1 error-handling).
// ---------------------------------------------------------------------------
export const PRE_QUIZ_QUESTIONS = [
  {
    key: 'concurrent_io_timing',
    label:
      'Three functions each make a network request that takes about 1 second to return. If you run them concurrently (properly), roughly how long until all three finish?',
    type: 'multiple_choice',
    options: ['~1 second', '~3 seconds', '~9 seconds', 'It depends only on your internet speed', "I'm not sure"]
  },
  {
    key: 'cpu_bound_concurrency',
    label:
      'On a single thread, you run 4 functions concurrently that each do heavy number-crunching (no waiting, just computation). Compared to running them one after another, the concurrent version will be:',
    type: 'multiple_choice',
    options: ['About 4× faster', 'Exactly 2× faster', 'Roughly the same speed, or slightly slower', 'Faster, but only on the first run', "I'm not sure"]
  },
  {
    key: 'forgotten_await',
    label:
      'In async code, you call an async function but forget to await it. What typically happens?',
    type: 'multiple_choice',
    options: [
      'It runs normally, just a little slower',
      'It automatically runs in the background',
      'The program crashes immediately',
      "The function doesn't actually run; you get an object representing the un-run work (often with a warning)",
      "I'm not sure"
    ]
  },
  {
    key: 'concurrent_completion_order',
    label:
      'Several tasks run concurrently, each finishing after a slightly different, unpredictable delay. Across repeated runs, the order in which they complete:',
    type: 'multiple_choice',
    options: ['Is always the same as the order they were started', 'Can differ from run to run', 'Is always the reverse of the start order', 'Is fixed by how the functions are named', "I'm not sure"]
  },
  {
    key: 'concurrent_error_propagation',
    label:
      "Several tasks run concurrently as a group. One of them raises an error that isn't caught inside that task. Without any special handling, what generally happens?",
    type: 'multiple_choice',
    options: [
      'The error is silently ignored and everything continues',
      'Only the failing task stops; the others are guaranteed to finish',
      "The error can propagate and disrupt the other tasks or the whole group unless it's explicitly caught",
      'The program automatically retries the failed task',
      "I'm not sure"
    ]
  },
  // --- added in v2 ---
  {
    key: 'except_type_mismatch',
    label:
      'A try/except block is written to catch ValueError, but the code inside raises a KeyError instead. What happens?',
    type: 'multiple_choice',
    options: [
      'The KeyError is caught anyway — except catches everything',
      'The KeyError is not caught and propagates up',
      'Both error types are caught automatically',
      'Python converts the KeyError into a ValueError',
      "I'm not sure"
    ]
  },
  {
    key: 'finally_semantics',
    label: 'In a try/except/finally block, when does the finally section run?',
    type: 'multiple_choice',
    options: [
      'Only if no exception occurred',
      'Only if an exception occurred',
      'Always — whether or not an exception occurred',
      'Only when the function returns normally',
      "I'm not sure"
    ]
  },
  {
    key: 'loop_error_stops_rest',
    label:
      "You process several independent items in a loop, and one item raises an exception you don't catch. What happens to the remaining items?",
    type: 'multiple_choice',
    options: [
      'They are all still processed',
      "The loop stops — the remaining items aren't processed",
      'Only the failed item is retried',
      'The remaining items are processed in reverse order',
      "I'm not sure"
    ]
  }
]

// ---------------------------------------------------------------------------
// MAIN — after both AI tasks, BEFORE the immediate transfer task.
// Per-domain (v2). confidence_without_ai_* forecasts transfer for that domain.
// ---------------------------------------------------------------------------
export const MID_SESSION_QUESTIONS = [
  {
    key: 'understanding_concurrency',
    label:
      "How well do you feel you understand Trio's concurrency features (nurseries, starting tasks, running functions at the same time)?",
    type: 'scale', min: 1, max: 7, minLabel: 'Not at all', maxLabel: 'Completely'
  },
  {
    key: 'understanding_error_handling',
    label:
      "How well do you feel you understand Trio's error-handling features (handling failures inside concurrent tasks)?",
    type: 'scale', min: 1, max: 7, minLabel: 'Not at all', maxLabel: 'Completely'
  },
  {
    key: 'confidence_without_ai_concurrency',
    label:
      "How confident are you that you could complete a NEW task using Trio's concurrency features without AI assistance?",
    type: 'scale', min: 1, max: 7, minLabel: 'Not at all confident', maxLabel: 'Extremely confident'
  },
  {
    key: 'confidence_without_ai_error_handling',
    label:
      "How confident are you that you could complete a NEW task using Trio's error-handling features without AI assistance?",
    type: 'scale', min: 1, max: 7, minLabel: 'Not at all confident', maxLabel: 'Extremely confident'
  }
]

// ---------------------------------------------------------------------------
// Per-task difficulty — call after each coding block.
// taskKey: 'a1' | 'b1' | 'transfer_immediate' | 'transfer_delayed'
// ---------------------------------------------------------------------------
export const taskDifficulty = (taskKey, taskLabel = 'this task') => [
  {
    key: `difficulty_${taskKey}`,
    label: `How difficult was ${taskLabel} for you?`,
    type: 'scale', min: 1, max: 7,
    minLabel: 'Very easy', maxLabel: 'Very difficult'
  }
]

// ---------------------------------------------------------------------------
// MAIN — after the immediate transfer task, before the quiz.
// Understanding repeated per domain (trajectory); confidence_quiz kept from v1.
// ---------------------------------------------------------------------------
export const POST_TASK_QUESTIONS = [
  {
    key: 'understanding_concurrency',
    label:
      "How well do you feel you understand Trio's concurrency features (nurseries, starting tasks, running functions at the same time)?",
    type: 'scale', min: 1, max: 7, minLabel: 'Not at all', maxLabel: 'Completely'
  },
  {
    key: 'understanding_error_handling',
    label:
      "How well do you feel you understand Trio's error-handling features (handling failures inside concurrent tasks)?",
    type: 'scale', min: 1, max: 7, minLabel: 'Not at all', maxLabel: 'Completely'
  },
  {
    key: 'confidence_quiz',
    label: 'How confident are you that you could correctly answer questions about how the Trio library works?',
    type: 'scale', min: 1, max: 7, minLabel: 'Not at all confident', maxLabel: 'Extremely confident'
  }
]

// ---------------------------------------------------------------------------
// MAIN — end-of-session exit survey (after the quiz).
// v1 verbatim except: honesty item names the no-AI blocks; two attention checks.
// ---------------------------------------------------------------------------
export const POST_SURVEY_QUESTIONS = [
  { key: 'age', label: 'What is your age?', type: 'radio',
    options: ['18–24', '25–30', '31–35', '36–45', '46+', 'Prefer not to say'] },
  { key: 'gender', label: 'What is your gender?', type: 'radio',
    options: ['Woman', 'Man', 'Non-binary', 'Prefer to self-describe', 'Prefer not to say'] },
  { key: 'gender_self_describe', label: 'If you prefer to self-describe, please specify:',
    type: 'text', placeholder: '', optional: true, showIf: { key: 'gender', value: 'Prefer to self-describe' } },
  { key: 'race_ethnicity', label: 'What is your race/ethnicity? (Select all that apply, or skip.)',
    type: 'text', placeholder: 'Optional', optional: true },
  { key: 'program_field', label: 'What is your current graduate program / field of study?',
    type: 'text', placeholder: 'e.g., MS in Computer Science', optional: true },
  { key: 'english_first_language', label: 'Is English your first language?',
    type: 'radio', options: ['Yes', 'No', 'Prefer not to say'] },
  { key: 'ai_reliance',
    label: 'During the tasks where an AI assistant was available, how much did you rely on it?',
    type: 'radio',
    options: ['Not at all', 'A little', 'A moderate amount', 'A lot', 'I delegated almost everything'] },
  { key: 'attention_check_1',
    label: "For data quality, please select 'A moderate amount' for this item.",
    type: 'radio',
    options: ['Not at all', 'A little', 'A moderate amount', 'A lot', 'I delegated almost everything'] },
  { key: 'used_external_resources',
    label:
      'During the parts where the AI assistant was NOT available (the concept quiz, the final programming task, and the quiz), did you use any outside resources not provided (e.g., external AI, web search beyond the docs, asking someone)?',
    type: 'radio', options: ['Yes', 'No', 'Prefer not to say'],
    subtitle: 'This is for research purposes only and will not affect your compensation. Honest answers help us interpret the results.' },
  { key: 'external_resources_detail', label: 'What did you use?',
    type: 'text', placeholder: 'e.g., ChatGPT, Google, notes...', optional: true,
    showIf: { key: 'used_external_resources', value: 'Yes' } },
  { key: 'attention_check_2',
    label: 'For this item, please select 3.',
    type: 'scale', min: 1, max: 7, minLabel: '1', maxLabel: '7' },
  { key: 'reflection_understanding',
    label: 'How did you feel about your understanding of Trio by the end? Anything that felt clear, or anything that still feels shaky?',
    type: 'text', placeholder: 'A sentence or two is plenty.', optional: true },
  { key: 'reflection_approach',
    label: 'Was there any difference in how you approached the tasks with the AI assistant versus without it?',
    type: 'text', placeholder: 'A sentence or two is plenty.', optional: true },
  { key: 'feedback',
    label: 'Any other feedback about the tasks or your experience?',
    type: 'text', placeholder: "e.g., what worked well, what felt frustrating, anything you'd change...", optional: true }
  // NOTE: interview opt-in removed from v1 — the day-7 probe is a required part
  // booked via the platform gate, not an opt-in. Re-add interview_interest only
  // if a separate qualitative follow-up is planned.
]

// ---------------------------------------------------------------------------
// PROBE (day 6–8) — before the delayed task. Domain injected by counterbalance.
// ---------------------------------------------------------------------------
export const probePre = (domainLabel) => [
  {
    key: 'probe_confidence',
    label:
      `Without looking anything up: how confident are you that you could complete a NEW task using ${domainLabel} right now, without AI assistance?`,
    type: 'scale', min: 1, max: 7, minLabel: 'Not at all confident', maxLabel: 'Extremely confident'
  }
]
export const PROBE_PRE_CONCURRENCY = probePre(
  "Trio's concurrency features (nurseries, starting tasks, running functions at the same time)")
export const PROBE_PRE_ERROR_HANDLING = probePre(
  "Trio's error-handling features (handling failures inside concurrent tasks)")

// PROBE — after the delayed task. taskDifficulty('transfer_delayed') runs first.
export const PROBE_POST_QUESTIONS = [
  { key: 'probe_used_external',
    label:
      "During today's task, did you use any outside resources not provided (e.g., external AI, web search beyond the docs, notes, asking someone)?",
    type: 'radio', options: ['Yes', 'No', 'Prefer not to say'],
    subtitle: 'This is for research purposes only and will not affect your compensation or bonus. Honest answers help us interpret the results.' },
  { key: 'probe_external_detail', label: 'What did you use?',
    type: 'text', placeholder: 'e.g., ChatGPT, Google, notes...', optional: true,
    showIf: { key: 'probe_used_external', value: 'Yes' } },
  { key: 'probe_reflection',
    label: 'Compared to a week ago, did this feel easier, harder, or about the same? Anything you noticed you had forgotten or retained?',
    type: 'text', placeholder: 'A sentence or two is plenty.', optional: true }
]
